import { tryToDetectDateFormat } from 'components/common-form/builder/form/node/field/Grid/pasting';
import { supportedDateFormats } from 'components/shared/DatePicker/utils';
import { useUserNotification } from 'hooks/useUserNotification';
import { IntlKey } from 'locales';
import { useCallback } from 'react';
import { parsePastedDateWithErrorMessage } from 'utils/date';

import { useSPIntl } from './useIntl';
import { useLocalDateFormat } from './useLocalDateFormat';

const TOAST_PASTE_KEY: IntlKey = 'toast.paste';
export function usePasteSingleDate(setData: (isoValue: Readonly<Nullable<string>>) => void) {
  const [dateFormat] = useLocalDateFormat();
  const { formatMessage, formatDisplayedDate } = useSPIntl();
  const notification = useUserNotification();
  return useCallback(
    (pastedText: string | undefined) => {
      if (pastedText === undefined) {
        return;
      }
      const allDateFormats = supportedDateFormats(dateFormat);
      const detectedDateFormat = tryToDetectDateFormat({
        dates: [pastedText],
        dateFormats: allDateFormats,
        formatMsg: formatMessage,
        onWarning: m => notification.warn({ title: { id: TOAST_PASTE_KEY }, content: [m] }),
        onError: m => notification.error({ title: { id: TOAST_PASTE_KEY }, content: [m] }),
      });
      if (detectedDateFormat !== null) {
        try {
          const value = parsePastedDateWithErrorMessage(pastedText, detectedDateFormat, (id, values) =>
            formatMessage(id, { ...values, column: 'date' }),
          );
          notification.success({
            title: { id: TOAST_PASTE_KEY },
            content: [
              {
                id: 'moreMenu.pasteFromClipboard.successDateOnly',
                values: { value: formatDisplayedDate(value) },
              },
            ],
          });
          setData(value);
        } catch {
          notification.error({ title: { id: TOAST_PASTE_KEY }, content: ['Technical error while parsing date'] });
        }
      }
    },
    [dateFormat, formatDisplayedDate, formatMessage, notification, setData],
  );
}
